import { getStorage, ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { Capacitor } from '@capacitor/core';
import { getFirestore } from 'firebase/firestore';
import { AuthStateHook, useAuthState as uas } from 'react-firebase-hooks/auth';
import firebase from 'firebase/compat/app';
import 'firebase/compat/auth';
import 'firebase/compat/firestore';
import { Auth, getAuth, indexedDBLocalPersistence, initializeAuth } from 'firebase/auth';

// Firebase configuration object
const firebaseConfig = {
  apiKey: process.env.API_KEY,
  authDomain: process.env.AUTH_DOMAIN,
  projectId: process.env.PROJECT_ID,
  storageBucket: process.env.STORAGE_BUCKET,
  messagingSenderId: process.env.MESSAGING_SENDER_ID,
  appId: process.env.APP_ID,
  measurementId: process.env.MEASUREMENT_ID,
};

// Initialize Firebase app if it hasn't been initialized yet
let app: firebase.app.App | null = null;

try {
  if (!firebase.apps.length) {
    app = firebase.initializeApp(firebaseConfig);
  } else {
    app = firebase.app();
  }
} catch (error) {
  console.error("Firebase app initialization error:", error);
  throw new Error("Failed to initialize Firebase app");
}

// Singleton pattern for Auth provider
let authProviderInstance: Auth | null = null;

const authProvider = (app: firebase.app.App) => {
  if (authProviderInstance) {
    return authProviderInstance;
  }

  // Initialize authentication based on the platform
  try {
    if (Capacitor.isNativePlatform()) {
      authProviderInstance = initializeAuth(app, {
        persistence: indexedDBLocalPersistence,
      });
    } else {
      authProviderInstance = firebase.auth() as unknown as Auth;
    }
  } catch (error) {
    console.error("Auth provider initialization error:", error);
    throw new Error("Failed to initialize Auth provider");
  }

  return authProviderInstance;
}

// Set up exports for Firebase services
export const auth = authProvider(app as firebase.app.App);

// export const vanillaFirbaseAuth = getAuth(app);

export const db = (() => {
  try {
    return getFirestore(app);
  } catch (error) {
    console.error("Firestore initialization error:", error);
    throw new Error("Failed to initialize Firestore");
  }
})();

export const storage = (() => {
  try {
    return getStorage(app);
  } catch (error) {
    console.error("Storage initialization error:", error);
    throw new Error("Failed to initialize Storage");
  }
})();

export const documentId = firebase.firestore.FieldPath.documentId;
export const serverTimestamp = firebase.firestore.FieldValue.serverTimestamp;
export const fbTimestamp = firebase.firestore.Timestamp;
export const fromMillis = firebase.firestore.Timestamp.fromMillis;
export const increment = firebase.firestore.FieldValue.increment;
export const firestore = firebase.firestore()
export const storageRef = ref;
export const storageUploadBytes = uploadBytes;
export const storageGetDownloadURL = getDownloadURL;


export const analytics = () => {
  if (typeof window !== 'undefined') {
    try {
      return firebase.analytics();
    } catch (error) {
      console.error("Analytics initialization error:", error);
      return null;
    }
  }
  return null;
};

// Type for useAuthState with optional parameters
export const useAuthState: (auth: Auth, options?: any) => AuthStateHook = uas;

export default firebase;
